import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 grid-bg">
      <div className="absolute inset-0 hero-glow" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md bg-[#0C1424] border border-slate-800 rounded-lg p-8"
      >
        <Link to="/" className="flex justify-center mb-6"><LogoFull size={92} /></Link>
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 size={40} className="text-green-400 mx-auto" />
            <h1 className="font-heading text-2xl font-semibold mt-4">Password reset!</h1>
            <p className="text-slate-400 text-sm mt-2">Redirecting you to log in…</p>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-invalid">
            <h1 className="font-heading text-2xl font-semibold">Invalid link</h1>
            <p className="text-slate-400 text-sm mt-2">This reset link is missing or broken. Please request a new one.</p>
            <Link to="/forgot-password" className="inline-block mt-6 text-cyanbrand hover:underline text-sm">Request a new link</Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold text-center">Choose a new password</h1>
            <p className="text-slate-400 text-sm text-center mt-1">Enter a new password for your account.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
              <div>
                <label className="text-sm text-slate-400">New password</label>
                <input
                  data-testid="reset-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 w-full bg-[#030812] border border-slate-800 text-white rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyanbrand transition-all"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400">Confirm password</label>
                <input
                  data-testid="reset-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="mt-1 w-full bg-[#030812] border border-slate-800 text-white rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyanbrand transition-all"
                  placeholder="••••••••"
                />
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
              <button
                data-testid="reset-submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold py-3 rounded-md transition-all disabled:opacity-60"
                style={{ boxShadow: "0 0 15px rgba(0,229,255,0.3)" }}
              >
                {loading && <Loader2 size={18} className="animate-spin" />} Reset password
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
